/**
 * useT — the translation hook.
 *
 * `const { t, locale } = useT();` then `t('nav.items.dashboard')`. Keys are a
 * TypeScript union of every dot-path in the catalog (`TranslationKey`), so an
 * unknown or mistyped key is a compile error and refactors stay honest. The
 * component re-renders on a language change because it subscribes to the locale
 * store.
 *
 * Resolution: active-locale catalog → French fallback (FALLBACK_LOCALE) → the
 * key itself (so a genuinely missing key is visible in the UI rather than blank).
 * `{{var}}` placeholders are interpolated from the optional `vars` argument.
 */

import { useCallback } from 'react';

import { FALLBACK_LOCALE, type Locale } from './config';
import { catalogs, type TranslationKeys } from './locales';
import { useLocaleStore } from './store';

/**
 * Union of every dot-path whose leaf is a string, derived from the catalog
 * shape — e.g. `'common.save' | 'nav.items.dashboard' | 'profile.prefs.theme'`.
 */
type PathInto<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${PathInto<T[K]>}`;
}[keyof T & string];

export type TranslationKey = PathInto<TranslationKeys>;

export type TVars = Record<string, string | number>;

/** Walk a dotted key into a catalog; return the string leaf or undefined. */
function resolve(catalog: TranslationKeys, key: string): string | undefined {
  let cur: unknown = catalog;
  for (const part of key.split('.')) {
    if (cur !== null && typeof cur === 'object' && part in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return typeof cur === 'string' ? cur : undefined;
}

/** Replace `{{name}}` placeholders with values from `vars`. */
export function interpolate(template: string, vars?: TVars): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match,
  );
}

/** Pure translator for a given locale (used by the hook and by tests). */
export function translate(locale: Locale, key: string, vars?: TVars): string {
  const raw =
    resolve(catalogs[locale], key) ?? resolve(catalogs[FALLBACK_LOCALE], key) ?? key;
  return interpolate(raw, vars);
}

export interface UseTranslation {
  t: (key: TranslationKey, vars?: TVars) => string;
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export function useT(): UseTranslation {
  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);

  const t = useCallback(
    (key: TranslationKey, vars?: TVars) => translate(locale, key, vars),
    [locale],
  );

  return { t, locale, setLocale };
}

/** Alias so the call site reads like the familiar react-i18next hook. */
export const useTranslation = useT;
